/* WOD.LOL mega menu — desktop <wl-mega> + mobile <wl-drawer> */
(() => {
  const hoverCapable = window.matchMedia('(hover: hover)');

  /* ---------------- Desktop ---------------- */
  class WlMega extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.wl-mega__item')];
      this.line = this.querySelector('.wl-mega__line');
      this.current = null;

      this.scrim = document.createElement('div');
      this.scrim.className = 'wl-scrim';
      document.body.appendChild(this.scrim);

      this.items.forEach((li) => {
        const top = li.querySelector('.wl-mega__top');
        const panel = li.querySelector('.wl-panel');

        li.addEventListener('mouseenter', () => {
          if (!hoverCapable.matches) return;
          this.moveLine(top);
          if (panel) this.scheduleOpen(li);
          else this.scheduleClose(120);
        });

        top.addEventListener('focus', () => {
          this.moveLine(top);
          if (panel) this.open(li);
          else this.close();
        });

        if (panel) {
          top.addEventListener('click', (e) => {
            // Touch screens >= 990px: first tap opens, second tap follows the link
            if (!hoverCapable.matches && this.current !== li) {
              e.preventDefault();
              this.moveLine(top);
              this.open(li);
            }
          });
          panel.addEventListener('mouseenter', () => clearTimeout(this.closeT));
        }
      });

      this.addEventListener('mouseleave', () => {
        this.scheduleClose(200);
        this.restLine();
      });
      this.addEventListener('mouseenter', () => clearTimeout(this.closeT));
      this.scrim.addEventListener('mouseenter', () => this.scheduleClose(60));
      this.scrim.addEventListener('click', () => this.close());

      this.onKey = (e) => {
        if (e.key !== 'Escape' || !this.current) return;
        const top = this.current.querySelector('.wl-mega__top');
        this.close();
        top.focus({ preventScroll: true });
      };
      document.addEventListener('keydown', this.onKey);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) {
          this.close();
          this.restLine();
        }
      });

      this.onResize = () => {
        this.restLine();
        this.updateOffset();
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', () => this.current && this.updateOffset(), { passive: true });

      // wait for fonts so the underline matches the label width
      (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => this.restLine());
    }

    disconnectedCallback() {
      this.scrim.remove();
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('resize', this.onResize);
    }

    updateOffset() {
      const bottom = Math.max(0, this.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--wl-bar-bottom', `${bottom}px`);
    }

    moveLine(top) {
      if (!this.line || !top) return;
      const label = top.querySelector('.wl-mega__top-label') || top;
      const barBox = this.line.parentElement.getBoundingClientRect();
      const box = label.getBoundingClientRect();
      this.line.style.width = `${box.width}px`;
      this.line.style.setProperty('--x', `${box.left - barBox.left}px`);
      this.line.classList.add('is-on');
    }

    restLine() {
      const active = this.current
        ? this.current.querySelector('.wl-mega__top')
        : this.querySelector('.wl-mega__top.is-active');
      if (active) this.moveLine(active);
      else this.line && this.line.classList.remove('is-on');
    }

    scheduleOpen(li) {
      clearTimeout(this.closeT);
      clearTimeout(this.openT);
      // small hover-intent delay on first open, instant when switching
      this.openT = setTimeout(() => this.open(li), this.current ? 0 : 90);
    }

    scheduleClose(delay) {
      clearTimeout(this.openT);
      clearTimeout(this.closeT);
      this.closeT = setTimeout(() => this.close(), delay);
    }

    open(li) {
      clearTimeout(this.closeT);
      if (this.current === li) return;
      const prev = this.current;
      const panel = li.querySelector('.wl-panel');
      if (prev) this.hide(prev, true);

      this.current = li;
      this.updateOffset();
      panel.hidden = false;
      panel.classList.remove('is-closing');
      panel.classList.toggle('is-swap', Boolean(prev));
      // two frames so the closed state is painted before transitioning
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.current === li) panel.classList.add('is-open');
      }));

      li.classList.add('is-open');
      li.querySelector('.wl-mega__top').setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.scrim.classList.add('is-on');
    }

    hide(li, instant) {
      const panel = li.querySelector('.wl-panel');
      li.classList.remove('is-open');
      li.querySelector('.wl-mega__top').setAttribute('aria-expanded', 'false');
      if (!panel) return;
      if (instant) {
        panel.classList.remove('is-open', 'is-closing');
        panel.hidden = true;
        return;
      }
      panel.classList.add('is-closing');
      panel.classList.remove('is-open');
      setTimeout(() => {
        if (this.current !== li && !panel.classList.contains('is-open')) {
          panel.hidden = true;
          panel.classList.remove('is-closing');
        }
      }, 320);
    }

    close() {
      clearTimeout(this.openT);
      if (!this.current) return;
      const li = this.current;
      this.current = null;
      this.hide(li, false);
      this.classList.remove('is-open');
      this.scrim.classList.remove('is-on');
    }
  }

  /* ---------------- Mobile drawer ---------------- */
  class WlDrawer extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.wl-burger');
      this.panel = this.querySelector('.wl-drawer');
      this.scrim = this.querySelector('.wl-drawer-scrim');
      if (!this.btn || !this.panel) return;

      // portal out of the sticky header so position:fixed is relative to the viewport
      document.body.appendChild(this.scrim);
      document.body.appendChild(this.panel);

      this.isOpen = false;
      this.btn.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.panel.querySelector('.wl-drawer__close').addEventListener('click', () => this.close());
      this.scrim.addEventListener('click', () => this.close());

      this.panel.querySelectorAll('.wl-acc__toggle').forEach((btn) => {
        btn.addEventListener('click', () => this.toggle(btn));
      });

      // close when navigating (also covers same-page anchors)
      this.panel.addEventListener('click', (e) => {
        const a = e.target.closest('a[href]');
        if (a && !e.defaultPrevented) setTimeout(() => this.close(), 50);
      });

      this.onKey = (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      };
      document.addEventListener('keydown', this.onKey);

      this.mq = window.matchMedia('(min-width: 990px)');
      this.onMq = (e) => e.matches && this.close();
      this.mq.addEventListener('change', this.onMq);

      this.bindSwipe();
    }

    disconnectedCallback() {
      this.panel && this.panel.remove();
      this.scrim && this.scrim.remove();
      document.removeEventListener('keydown', this.onKey);
      this.mq && this.mq.removeEventListener('change', this.onMq);
      document.documentElement.classList.remove('wl-lock');
    }

    open() {
      clearTimeout(this.hideT);
      this.isOpen = true;
      this.panel.hidden = false;
      this.scrim.hidden = false;
      this.panel.style.transform = '';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.panel.classList.add('is-open');
        this.scrim.classList.add('is-open');
      }));
      document.documentElement.classList.add('wl-lock');
      this.btn.classList.add('is-active');
      this.btn.setAttribute('aria-expanded', 'true');
      setTimeout(() => this.panel.querySelector('.wl-drawer__close').focus({ preventScroll: true }), 350);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.classList.remove('is-open', 'is-dragging');
      this.scrim.classList.remove('is-open');
      this.panel.style.transform = '';
      this.scrim.style.opacity = '';
      document.documentElement.classList.remove('wl-lock');
      this.btn.classList.remove('is-active');
      this.btn.setAttribute('aria-expanded', 'false');
      this.hideT = setTimeout(() => {
        if (this.isOpen) return;
        this.panel.hidden = true;
        this.scrim.hidden = true;
      }, 600);
      this.btn.focus({ preventScroll: true });
    }

    toggle(btn) {
      const willOpen = btn.getAttribute('aria-expanded') !== 'true';
      this.panel.querySelectorAll('.wl-acc__toggle[aria-expanded="true"]').forEach((b) => {
        if (b !== btn) b.setAttribute('aria-expanded', 'false');
      });
      btn.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        // keep the opened row in view once the accordion has expanded
        setTimeout(() => {
          const scroller = this.panel.querySelector('.wl-drawer__scroll');
          const top = btn.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
          if (top < 0 || top > scroller.clientHeight * 0.45) {
            scroller.scrollBy({ top: top - 8, behavior: 'smooth' });
          }
        }, 320);
      }
    }

    trap(e) {
      const focusables = [...this.panel.querySelectorAll('a[href], button:not([disabled])')].filter(
        (el) => el.offsetParent !== null
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    bindSwipe() {
      let x0 = 0;
      let y0 = 0;
      let dx = 0;
      let dragging = false;
      let locked = false;

      this.panel.addEventListener('touchstart', (e) => {
        if (e.target.closest('.wl-quick, .wl-hscroll')) return; // horizontal carousels scroll normally
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
        dx = 0;
        dragging = true;
        locked = false;
      }, { passive: true });

      this.panel.addEventListener('touchmove', (e) => {
        if (!dragging) return;
        const mx = e.touches[0].clientX - x0;
        const my = e.touches[0].clientY - y0;
        if (!locked) {
          if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
          locked = true;
          if (Math.abs(my) > Math.abs(mx) || mx > 0) {
            dragging = false;
            return;
          }
          this.panel.classList.add('is-dragging');
        }
        dx = Math.min(0, mx);
        this.panel.style.transform = `translateX(${dx}px)`;
        this.scrim.style.opacity = String(1 + dx / this.panel.offsetWidth);
      }, { passive: true });

      this.panel.addEventListener('touchend', () => {
        if (!dragging) return;
        dragging = false;
        this.panel.classList.remove('is-dragging');
        if (dx < -80) {
          this.close();
        } else {
          this.panel.style.transform = '';
          this.scrim.style.opacity = '';
        }
      });
    }
  }

  if (!customElements.get('wl-mega')) customElements.define('wl-mega', WlMega);
  if (!customElements.get('wl-drawer')) customElements.define('wl-drawer', WlDrawer);
})();
